import type { Areas } from "$root/functional-areas/acl/checkers/all";
import { spawnShareDialogSmart, type EntityMetadata } from "$root/functional-areas/acl/spawn-share-dialog";
import { updateAclWithFeedbacks } from "$root/functional-areas/acl/spawn-share-dialog/update-acl-with-feedbacks";
import { getCurrentUser } from "$root/functional-areas/user";
import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import {
	BulkActionReviewSections,
	checkBulkActionAvailability,
	spawnBulkActionReviewDialog,
	type BulkActionValidator,
	type ReasonToMessageMap,
	type ReviewSectionLabels,
} from "./common";

type AclProviderResult = {
	customerEntityAcl?: Array<RichAccessControl>;
	linkedAclEntities?: Array<{ identifier?: string; name?: string }>;
};

export type SpawnBulkShareActionParams<Entity extends { name?: string }, Reason extends string> = {
	reasonKeys: readonly Reason[];
	validator: BulkActionValidator<Entity, Reason>;
	entities: Entity[];
	reasonToMessage: ReasonToMessageMap<Reason>;
	labels: ReviewSectionLabels;
	getRedirectUrl: (entityUuids: string[]) => string;
	area: Areas;
	customerAndLinkedAclProvider: (available: Entity[]) => () => Promise<Array<AclProviderResult>>;
	saveAclHandler: (
		available: Entity[],
		permission: { aclByEntity: Record<string, RichAccessControl[]> },
	) => Promise<void>;
	getEntityMetadata: (entity: Entity) => EntityMetadata;
	hideRedirectUrl?: boolean;
	showEntityNames?: boolean;
	/**
	 * Called once the user committed to sharing `available` — i.e. past the review-and-confirm dialog,
	 * before the share dialog opens and before any request is made. This is the intent, not the
	 * outcome: it fires even if the user then abandons the share dialog or the ACL save fails. Meant
	 * for analytics, so it must not throw.
	 */
	onIntent?: (available: Entity[]) => void;
};

export async function spawnBulkShareAction<Entity extends { name?: string }, Reason extends string>(
	params: SpawnBulkShareActionParams<Entity, Reason>,
): Promise<void> {
	const {
		reasonKeys,
		validator,
		entities,
		reasonToMessage,
		labels,
		getRedirectUrl,
		area,
		customerAndLinkedAclProvider,
		saveAclHandler,
		getEntityMetadata,
		hideRedirectUrl,
		showEntityNames,
		onIntent,
	} = params;

	const availabilityResult = checkBulkActionAvailability(reasonKeys, validator, entities);
	const [available, unavailable] = availabilityResult;

	async function onContinue() {
		onIntent?.(available);

		const user = getCurrentUser();
		const entityMetadata = available.map(getEntityMetadata);

		await spawnShareDialogSmart({
			multiple: true,
			area,
			header: "Share",
			userId: user.id,
			customerAndLinkedAclProvider: customerAndLinkedAclProvider(available),
			entityMetadata,
			async saveAclHandler(permission) {
				await updateAclWithFeedbacks(() => saveAclHandler(available, { aclByEntity: permission.aclByEntity }), {
					multi: true,
				});
			},
		});
	}

	if (!unavailable) {
		await onContinue();
		return;
	}

	spawnBulkActionReviewDialog({
		initialState: null,
		header: "Share: review and confirm",
		children: (
			<BulkActionReviewSections
				availabilityResult={availabilityResult}
				reasonToMessage={reasonToMessage}
				getRedirectUrl={getRedirectUrl}
				hideRedirectUrl={hideRedirectUrl}
				showEntityNames={showEntityNames}
				labels={labels}
			/>
		),
		canConfirm: available.length > 0,
		onContinue,
	});
}
